"use client";

import { useEffect, useState, useRef } from "react";
import { isDocumentDefined } from "@huub-web/utils";

type UseRotationParallaxOptions = {
  /**
   * Rotation intensity factor (0-1)
   * 0.1 means the element rotates 10% of the scroll distance in degrees
   */
  intensity?: number;
  /**
   * Whether to enable the rotation parallax effect
   */
  enabled?: boolean;
  /**
   * Base rotation in degrees (applied before parallax rotation)
   */
  baseRotation?: number;
  /**
   * Maximum rotation angle in degrees (constrains the rotation range)
   */
  maxRotation?: number;
};

/**
 * Hook that creates a rotation-based parallax scrolling effect for an element
 * Makes the element rotate based on scroll position relative to viewport center
 */
export const useRotationParallax = <T extends Element = HTMLElement>(
  options: UseRotationParallaxOptions = {},
) => {
  const {
    intensity = 0.05,
    enabled = true,
    baseRotation = 0,
    maxRotation = 15,
  } = options;

  const [transform, setTransform] = useState(`rotate(${baseRotation}deg)`);
  const elementRef = useRef<T | null>(null);

  const ref = (node: T | null) => {
    elementRef.current = node;
  };

  useEffect(() => {
    if (!enabled || !isDocumentDefined()) {
      return;
    }

    const handleScroll = () => {
      if (!elementRef.current) {
        return;
      }

      const rect = elementRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Calculate how much of the element is visible
      // Positive when element is above viewport center, negative when below
      const elementCenter = rect.top + rect.height / 2;
      const viewportCenter = windowHeight / 2;
      const distanceFromCenter = elementCenter - viewportCenter;

      // Calculate rotation offset based on distance from viewport center
      const rawRotationOffset = distanceFromCenter * intensity;

      // Constrain rotation to prevent excessive spinning
      const constrainedRotation = Math.max(
        -maxRotation,
        Math.min(maxRotation, rawRotationOffset),
      );

      const totalRotation = baseRotation + constrainedRotation;

      setTransform(`rotate(${totalRotation}deg)`);
    };

    // Initial calculation
    handleScroll();

    // Add scroll listener with throttling for performance
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", throttledScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", throttledScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [intensity, enabled, baseRotation, maxRotation]);

  return {
    ref,
    style: {
      transform,
      transition: enabled ? undefined : "transform 0.3s ease-out",
    },
  };
};
